import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { X, Plus, Trash2 } from 'react-feather';
import { ToolbarButton } from '../../components/ConfigPages';
import { InlineSelect, InlineCheckbox } from '../../components/InlineTable';
import { createRevenueBasisGroup, updateRevenueBasisGroup } from '../../utils/revenueBasisApi';

// New members default to includeDescendants = true (matches the DB default).
const emptyMember = () => ({ typeClassificationId: null, includeDescendants: true });

/**
 * Create / edit a revenue basis group: code, name, description and the member
 * revenue classifications (each with an "also count children" flag).
 */
export default function RevenueBasisModal({ open, group, orgId, classificationOptions, onClose, onSaved }) {
  const editing = !!group;
  const [form, setForm] = useState({ code: '', name: '', description: '', members: [emptyMember()] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: group?.code || '',
      name: group?.name || '',
      description: group?.description || '',
      members: group?.members?.length
        ? group.members.map((m) => ({ typeClassificationId: m.typeClassificationId, includeDescendants: m.includeDescendants === true }))
        : [emptyMember()],
    });
  }, [open, group]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setMember = (i, patch) => setForm((f) => ({ ...f, members: f.members.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));
  const addMember = () => setForm((f) => ({ ...f, members: [...f.members, emptyMember()] }));
  const removeMember = (i) => setForm((f) => ({ ...f, members: f.members.length === 1 ? [emptyMember()] : f.members.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const code = form.code.trim();
    const name = form.name.trim();
    const members = form.members.filter((m) => m.typeClassificationId).map((m) => ({ typeClassificationId: m.typeClassificationId, includeDescendants: !!m.includeDescendants }));
    if (!code || !name) { toast.error('Code and name are required'); return; }
    if (members.length === 0) { toast.error('Add at least one revenue classification'); return; }
    const payload = { orgId, locationId: null, code, name, description: form.description.trim() || null, members };
    setSaving(true);
    try {
      const saved = editing ? await updateRevenueBasisGroup(group.id, payload) : await createRevenueBasisGroup(payload);
      toast.success(editing ? 'Saved' : 'Revenue basis created');
      onSaved(saved);
      onClose();
    } catch (err) {
      console.error('Error saving revenue basis group:', err);
      toast.error(`Could not ${editing ? 'save' : 'create'} the revenue basis — check the code is unique and try again`);
    } finally {
      setSaving(false);
    }
  };

  // Each classification can appear once; hide already-picked ones from the other rows.
  const optionsFor = (i) => {
    const taken = new Set(form.members.filter((_, j) => j !== i).map((m) => m.typeClassificationId).filter(Boolean));
    return classificationOptions.filter((o) => !taken.has(o.value));
  };

  return (
    <div className="cp-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cp-modal" role="dialog" aria-modal="true" aria-label={editing ? 'Edit revenue basis' : 'Add revenue basis'}>
        <form onSubmit={submit}>
          <div className="cp-modal-head">
            <h2>{editing ? 'Edit revenue basis' : 'Add revenue basis'}</h2>
            <ToolbarButton small iconOnly variant="ghost" icon={X} onClick={onClose} aria-label="Close" />
          </div>
          <div className="cp-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)', gap: 12 }}>
              <label className="cp-field">
                <span className="cp-label">Code</span>
                <input className="cp-input cp-mono" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="BANQUET_ALL" autoFocus />
              </label>
              <label className="cp-field">
                <span className="cp-label">Name</span>
                <input className="cp-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Banquet — all revenue" />
              </label>
            </div>
            <label className="cp-field">
              <span className="cp-label">Description</span>
              <textarea className="cp-input" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What labor is measured against this revenue (optional)" />
            </label>
            <div className="cp-field">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="cp-label">Revenue classifications</span>
                <ToolbarButton small variant="ghost" icon={Plus} onClick={addMember}>Add classification</ToolbarButton>
              </div>
              <div className="cp-help">Descendants = also count child classifications, e.g. Kitchen under F&amp;B.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                {form.members.map((m, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto', gap: 10, alignItems: 'center', border: '1px solid var(--lodge-onyx-100)', borderRadius: 8, padding: '2px 4px 2px 0' }}>
                    <InlineSelect rowActive value={m.typeClassificationId} options={optionsFor(i)} onChange={(v) => setMember(i, { typeClassificationId: v ?? null })} placeholder="Choose a revenue classification…" minWidth={320} ariaLabel="Revenue classification" />
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--lodge-onyx-600)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                      <InlineCheckbox checked={!!m.includeDescendants} onChange={(e) => setMember(i, { includeDescendants: e.target.checked })} />
                      Descendants
                    </label>
                    <ToolbarButton small iconOnly variant="ghost" icon={Trash2} onClick={() => removeMember(i)} title="Remove classification" aria-label="Remove classification" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="cp-modal-foot">
            <ToolbarButton variant="ghost" onClick={onClose} disabled={saving}>Cancel</ToolbarButton>
            <ToolbarButton variant="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save' : 'Create'}</ToolbarButton>
          </div>
        </form>
      </div>
    </div>
  );
}
